import { create } from 'zustand';

/**
 * The one signal that lets an editor keyed to a piece tell apart the two ways
 * the group it is mounted on can change what it points at: a rep-picked SWAP
 * onto a different Library piece (must reset the editor, same as always)
 * versus the group being silently repointed — copy-on-write forking a Library
 * identity `S` onto THIS SAME PLAY's fresh copy `C` of `S`, a copy save, a
 * fan-out, or the rep's "Reload to see their change" after a stale refusal.
 * The rep never chose any of those, so discarding what they typed just to
 * make them type it again is the bug this store exists to fix.
 *
 * `ensure-play-owned.ts#adoptPlayLadderMoves` is the ONE place a ladder
 * change is adopted on the client, so it is the one caller of
 * {@link signalLadderMovesAdopted}.
 *
 * Plan 2026-09-28-007 U5 (R7) — a slot holds the SET of moves one adopt
 * made, not a single pair: a fan-out can move several groups of this play
 * onto several copies at once, and with a single slot the last-signalled
 * pair named some OTHER group, so the origin group's editor read its own
 * transition as a swap and closed Steps (reflection r-4, Codex b1-3). The
 * whole set is signalled BEFORE the ladder lands, so the consuming editor
 * reads it in the same commit its id prop changes, and compares the exact
 * `(fromId, toId)` pair it observed — membership, never "the last one". A
 * stale or unrelated adoption can never false-match: the ids have to agree
 * on both ends.
 *
 * Plan 2026-09-29-001 U2 (R14; Codex round-4 B1) — PER KIND, and no longer
 * sequence-only. `SegmentationConditionsEditor`'s `useDraftState` key was
 * the segmentation id itself, and an adopted SEGMENT move changes that id —
 * so the rep's unsaved filters were reseeded away by their own Reload. A
 * segment move and a sequence move are signalled into separate slots so
 * neither editor can ever read the other's pair.
 */
export type LadderAdoptionKind = 'segmentation' | 'sequence';

export interface LadderAdoption {
  fromId: string;
  toId: string;
}

interface LadderAdoptionState {
  lastAdoption: Record<LadderAdoptionKind, LadderAdoption[]>;
}

/** Exported per the store convention — reset idiom is `useLadderAdoptionStore.setState(initialState, true)`. */
export const initialState: LadderAdoptionState = {
  lastAdoption: { segmentation: [], sequence: [] },
};

export const useLadderAdoptionStore = create<LadderAdoptionState>(() => ({
  lastAdoption: { ...initialState.lastAdoption },
}));

/** Called once per ladder adopt with every move it made for `kind`, from `ensure-play-owned.ts#adoptPlayLadderMoves` alone. */
export function signalLadderMovesAdopted(kind: LadderAdoptionKind, moves: readonly LadderAdoption[]): void {
  useLadderAdoptionStore.setState((state) => ({
    lastAdoption: { ...state.lastAdoption, [kind]: moves.map(({ fromId, toId }) => ({ fromId, toId })) },
  }));
}

/**
 * An editor's one read: was THIS EXACT `(fromId, toId)` id transition one of
 * the `kind` moves this play just adopted, rather than a rep-picked swap?
 * `undefined` on either side (no previous id yet, or nothing pointed at) is
 * never a match.
 */
export function wasLadderMoveAdopted(
  kind: LadderAdoptionKind,
  fromId: string | undefined,
  toId: string | undefined,
): boolean {
  if (fromId === undefined || toId === undefined) return false;
  return useLadderAdoptionStore
    .getState()
    .lastAdoption[kind].some((move) => move.fromId === fromId && move.toId === toId);
}

/** The Play-scoped reset `PlayFlowShell` performs alongside its other flow stores. */
export function clearLadderAdoptionSignal(): void {
  useLadderAdoptionStore.setState({ lastAdoption: { segmentation: [], sequence: [] } });
}
